import React from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { Mail, MessageCircle, MapPin, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import PageHero from "../components/PageHero";
import Footer from "../components/Footer";
import { useI18n } from "../lib/i18n";

export default function Contact() {
  const { t } = useI18n();
  const { register, handleSubmit, formState: { errors }, reset } = useForm({
    defaultValues: {
      name: "",
      email: "",
      message: "",
    },
  });

  const onSubmit = async (data) => {
    try {
      // Simulate form submission (replace with actual API endpoint)
      console.log("Form submitted:", data);
      toast.success("Message sent successfully! We'll get back to you soon.");
      reset();
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    }
  };

  return (
    <div style={{ background: "#080808", color: "#F5F5F5", minHeight: "100vh" }}>
      <Header />

      {/* Hero Section */}
      <PageHero
        eyebrow={t("nav_contact")}
        title={t("get_in_touch")}
        subtitle="Have questions about Orello? Want to partner with us? Reach out and we'll respond within 4 hours on weekdays."
        cta1Text="Email Us"
        cta1Link="mailto:hello@orello.in"
        hasGlow={true}
        gradientWord="help"
      />

      {/* Contact Content */}
      <section style={{ background: "#0B0B14", padding: "4rem 0" }}>
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2
                className="font-display font-bold mb-8"
                style={{ fontSize: "1.5rem", color: "#F5F5F5" }}
              >
                Send us a message
              </h2>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Name Field */}
                <div>
                  <label
                    htmlFor="name"
                    style={{
                      display: "block",
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      marginBottom: "0.5rem",
                      color: "rgba(245,245,245,0.7)",
                    }}
                  >
                    Full Name
                  </label>
                  <input
                    id="name"
                    type="text"
                    placeholder="Anjali Sharma"
                    {...register("name", { required: "Name is required" })}
                    style={{
                      width: "100%",
                      padding: "0.875rem 1rem",
                      borderRadius: 12,
                      background: "rgba(255,255,255,0.04)",
                      border: `1px solid ${errors.name ? "rgba(255,100,100,0.5)" : "rgba(255,255,255,0.1)"}`,
                      color: "#F5F5F5",
                      fontSize: "1rem",
                      transition: "all 0.2s ease",
                      outline: "none",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "rgba(249,115,22,0.5)";
                      e.target.style.background = "rgba(255,255,255,0.06)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "rgba(255,255,255,0.1)";
                      e.target.style.background = "rgba(255,255,255,0.04)";
                    }}
                  />
                  {errors.name && (
                    <p style={{ fontSize: "0.8rem", color: "#ff6464", marginTop: "0.25rem" }}>
                      {errors.name.message}
                    </p>
                  )}
                </div>

                {/* Email Field */}
                <div>
                  <label
                    htmlFor="email"
                    style={{
                      display: "block",
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      marginBottom: "0.5rem",
                      color: "rgba(245,245,245,0.7)",
                    }}
                  >
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    placeholder="hello@orello.in"
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address",
                      },
                    })}
                    style={{
                      width: "100%",
                      padding: "0.875rem 1rem",
                      borderRadius: 12,
                      background: "rgba(255,255,255,0.04)",
                      border: `1px solid ${errors.email ? "rgba(255,100,100,0.5)" : "rgba(255,255,255,0.1)"}`,
                      color: "#F5F5F5",
                      fontSize: "1rem",
                      transition: "all 0.2s ease",
                      outline: "none",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "rgba(249,115,22,0.5)";
                      e.target.style.background = "rgba(255,255,255,0.06)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "rgba(255,255,255,0.1)";
                      e.target.style.background = "rgba(255,255,255,0.04)";
                    }}
                  />
                  {errors.email && (
                    <p style={{ fontSize: "0.8rem", color: "#ff6464", marginTop: "0.25rem" }}>
                      {errors.email.message}
                    </p>
                  )}
                </div>

                {/* Message Field */}
                <div>
                  <label
                    htmlFor="message"
                    style={{
                      display: "block",
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      marginBottom: "0.5rem",
                      color: "rgba(245,245,245,0.7)",
                    }}
                  >
                    Message
                  </label>
                  <textarea
                    id="message"
                    placeholder="Tell us how we can help..."
                    rows={5}
                    {...register("message", { required: "Message is required" })}
                    style={{
                      width: "100%",
                      padding: "0.875rem 1rem",
                      borderRadius: 12,
                      background: "rgba(255,255,255,0.04)",
                      border: `1px solid ${errors.message ? "rgba(255,100,100,0.5)" : "rgba(255,255,255,0.1)"}`,
                      color: "#F5F5F5",
                      fontSize: "1rem",
                      transition: "all 0.2s ease",
                      outline: "none",
                      resize: "vertical",
                      fontFamily: "inherit",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "rgba(249,115,22,0.5)";
                      e.target.style.background = "rgba(255,255,255,0.06)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "rgba(255,255,255,0.1)";
                      e.target.style.background = "rgba(255,255,255,0.04)";
                    }}
                  />
                  {errors.message && (
                    <p style={{ fontSize: "0.8rem", color: "#ff6464", marginTop: "0.25rem" }}>
                      {errors.message.message}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="btn-press w-full inline-flex items-center justify-center gap-2 font-medium px-7 py-4 rounded-full text-white"
                  style={{
                    background: "linear-gradient(135deg, #F97316 0%, #8B5CF6 100%)",
                    fontSize: "1rem",
                    boxShadow: "0 8px 32px rgba(139,92,246,0.25)",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  Send Message <ArrowRight className="h-4 w-4" />
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <h2
                className="font-display font-bold mb-8"
                style={{ fontSize: "1.5rem", color: "#F5F5F5" }}
              >
                Other ways to reach us
              </h2>

              <div className="space-y-8">
                {/* Email */}
                <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start" }}>
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 12,
                      background: "rgba(249,115,22,0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Mail size={24} style={{ color: "#F97316" }} />
                  </div>
                  <div>
                    <h3
                      className="font-display font-semibold mb-1"
                      style={{ color: "#F5F5F5" }}
                    >
                      Email
                    </h3>
                    <a
                      href="mailto:help@orello.in"
                      style={{ color: "rgba(245,245,245,0.6)", textDecoration: "none" }}
                      className="hover:text-white transition-colors"
                    >
                      help@orello.in
                    </a>
                    <p style={{ fontSize: "0.85rem", color: "rgba(245,245,245,0.4)", marginTop: "0.25rem" }}>
                      We reply within 4 hours on weekdays
                    </p>
                  </div>
                </div>

                {/* WhatsApp */}
                <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start" }}>
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 12,
                      background: "rgba(249,115,22,0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <MessageCircle size={24} style={{ color: "#F97316" }} />
                  </div>
                  <div>
                    <h3
                      className="font-display font-semibold mb-1"
                      style={{ color: "#F5F5F5" }}
                    >
                      WhatsApp
                    </h3>
                    <a
                      href="https://wa.me/919876543210"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "rgba(245,245,245,0.6)", textDecoration: "none" }}
                      className="hover:text-white transition-colors"
                    >
                      +91 98765 43210
                    </a>
                    <p style={{ fontSize: "0.85rem", color: "rgba(245,245,245,0.4)", marginTop: "0.25rem" }}>
                      Available 9 AM – 6 PM IST
                    </p>
                  </div>
                </div>

                {/* Address */}
                <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start" }}>
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 12,
                      background: "rgba(249,115,22,0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <MapPin size={24} style={{ color: "#F97316" }} />
                  </div>
                  <div>
                    <h3
                      className="font-display font-semibold mb-1"
                      style={{ color: "#F5F5F5" }}
                    >
                      Office
                    </h3>
                    <p style={{ color: "rgba(245,245,245,0.6)", lineHeight: 1.6 }}>
                      Indiranagar, Bengaluru<br />
                      Karnataka, India — 560008
                    </p>
                  </div>
                </div>
              </div>

              {/* FAQ Link */}
              <div
                style={{
                  marginTop: "2rem",
                  padding: "1.5rem",
                  borderRadius: 16,
                  background: "rgba(249,115,22,0.08)",
                  border: "1px solid rgba(249,115,22,0.2)",
                }}
              >
                <p style={{ color: "rgba(245,245,245,0.6)", marginBottom: "0.75rem" }}>
                  Have a quick question?
                </p>
                <Link
                  to="/app/help"
                  style={{ color: "#F97316", textDecoration: "none", fontWeight: 600 }}
                  className="hover:text-orange-400 transition-colors"
                >
                  Check our Help & Support →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
